import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { TranslateService } from '@ngx-translate/core';
import { EMPTY, fromEvent, Observable, ReplaySubject } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

declare const embeddedservice_bootstrap: any;

@Injectable({
  providedIn: 'root',
})
export class SfChatService {
  private static scriptAdded = false;

  private readonly scriptUrl = environment.sfChatSiteUrl + 'assets/js/bootstrap.min.js';

  private readonly document: Document = inject(DOCUMENT);
  private readonly translate: TranslateService = inject(TranslateService);

  private readonly scriptLoaded$ = new ReplaySubject<void>(1);

  public readonly scriptLoaded: Observable<void> = this.scriptLoaded$
    .asObservable()
    .pipe(shareReplay(1));

  constructor() {
    this.loadScript();
  }

  private loadScript(): void {
    if (typeof this.document === 'undefined') {
      return;
    }

    if (SfChatService.scriptAdded) {
      this.scriptLoaded$.next();
      this.scriptLoaded$.complete();
      return;
    }

    SfChatService.scriptAdded = true;

    const script = this.document.createElement('script');
    script.type = 'text/javascript';
    script.src = this.scriptUrl;
    script.onload = () => {
      this.scriptLoaded$.next();
      this.scriptLoaded$.complete();
    };
    script.onerror = err => {
      this.scriptLoaded$.error(err);
    };
    this.document.body.appendChild(script);
  }

  public initEmbeddedMessaging(targetElement?: HTMLElement): Observable<void> {
    const initialized$ = new ReplaySubject<void>(1);

    this.scriptLoaded.pipe(take(1)).subscribe({
      next: () => {
        try {
          const locale = this.translate.getCurrentLang();
          embeddedservice_bootstrap.settings.language = locale;
          embeddedservice_bootstrap.settings.displayMode = 'inline';
          if (targetElement) {
            embeddedservice_bootstrap.settings.targetElement = targetElement;
          }

          this.waitForNextMessagingReady().subscribe(() => {
            embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
              language: locale ?? 'en',
            });
          });

          embeddedservice_bootstrap.init(
            environment.sfChatOrgId,
            environment.sfChatDeploymentName,
            environment.sfChatSiteUrl,
            {
              scrt2URL: environment.sfChatScrt2Url,
            }
          );

          initialized$.next();
          initialized$.complete();
        } catch (err) {
          console.error('SfChatService: Error loading Embedded Messaging: ', err);
          initialized$.error(err);
        }
      },
      error: err => initialized$.error(err),
    });

    return initialized$.asObservable();
  }

  /**
   * Emits once on the next `onEmbeddedMessagingReady` window event, then completes
   * and removes the event listener.
   */
  public waitForNextMessagingReady(): Observable<boolean> {
    const win = this.document?.defaultView;
    if (!win) {
      return EMPTY;
    }
    return fromEvent(win, 'onEmbeddedMessagingReady').pipe(
      take(1),
      map(() => true)
    );
  }
}
